import { getJwtCookie } from '../../../../../util/jwtCookie';
import { getCurrentVoiceRetailerRoomId, getCurrentVoiceRetailerUserId } from '../config/voiceEnvConfig';
import getOrCreateVoiceTabSessionId from '../webrtc/voiceTabSession';

/**
 * Decode a JWT payload in the browser without verifying the signature.
 * voice-service verifies HS512 with the shared Mercately secret.
 *
 * @param {string} token
 * @returns {Record<string, unknown>|null}
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = typeof atob === 'function'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('utf8');
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload
      : null;
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, unknown>|null|undefined} payload
 * @param {string[]} keys
 * @returns {string}
 */
export function pickJwtClaim(payload, keys) {
  if (!payload) return '';
  for (let index = 0; index < keys.length; index += 1) {
    const value = payload[keys[index]];
    if (value != null && value !== '') {
      return String(value);
    }
  }
  return '';
}

/**
 * Session JWT already issued by Mercately (`jwt_token` cookie / refresh).
 *
 * @returns {Promise<string>}
 */
export async function getMercatelySessionJwt() {
  const token = await getJwtCookie();
  if (!token || typeof token !== 'string') return '';
  try {
    return decodeURIComponent(token);
  } catch {
    return token;
  }
}

/**
 * Tenant/agent identity for voice-service. Prefer JWT claims over ENV/Redux.
 *
 * @param {string} token
 * @param {{ retailerId?: string, retailerUserId?: string }} [fallbacks]
 * @returns {{ retailerId: string, retailerUserId: string, claims: Record<string, unknown>|null }}
 */
export function getVoiceIdentityFromJwt(token, fallbacks = {}) {
  const claims = decodeJwtPayload(token);
  const retailerId = pickJwtClaim(claims, ['retailer_id', 'retailerId'])
    || fallbacks.retailerId
    || '';
  const retailerUserId = pickJwtClaim(claims, ['retailer_user_id', 'retailerUserId'])
    || fallbacks.retailerUserId
    || '';

  return { retailerId, retailerUserId, claims };
}

function sessionFallbacks() {
  return {
    retailerId: getCurrentVoiceRetailerRoomId(),
    retailerUserId: getCurrentVoiceRetailerUserId()
  };
}

/**
 * HTTP `Authorization: Bearer <jwt>` for voice-service. Throws if the session JWT is missing.
 *
 * @param {Record<string, string>} [baseHeaders]
 * @returns {Promise<Record<string, string>>}
 */
export async function buildVoiceHttpAuthHeaders(baseHeaders = {}) {
  const token = await getMercatelySessionJwt();
  if (!token) {
    const error = new Error('unauthorized');
    error.status = 401;
    throw error;
  }

  return {
    ...baseHeaders,
    Authorization: `Bearer ${token}`
  };
}

/**
 * Socket.IO handshake auth. JWT is required; without it voice-service disconnects.
 *
 * @returns {Promise<{
 *   token?: string,
 *   accessToken?: string,
 *   tab_session_id: string,
 *   tabSessionId: string,
 *   retailer_user_id?: string,
 *   retailerId?: string
 * }>}
 */
export async function buildVoiceSocketAuthPayload() {
  const token = await getMercatelySessionJwt();
  const { retailerId, retailerUserId } = getVoiceIdentityFromJwt(token, sessionFallbacks());
  const tabSessionId = getOrCreateVoiceTabSessionId();
  const auth = {
    tab_session_id: tabSessionId,
    tabSessionId
  };

  if (token) {
    auth.token = token;
    auth.accessToken = token;
  }
  if (retailerUserId) {
    auth.retailer_user_id = retailerUserId;
  }
  if (retailerId) {
    auth.retailerId = retailerId;
  }

  return auth;
}

/**
 * `join_room.room` must be the JWT `retailer_id` (not retailer_user_id).
 *
 * @param {string} [fallbackRoomId]
 * @returns {Promise<{ room: string, tab_session_id?: string, retailer_user_id?: string }>}
 */
export async function buildVoiceJoinRoomPayload(fallbackRoomId = '') {
  const token = await getMercatelySessionJwt();
  const { retailerId, retailerUserId } = getVoiceIdentityFromJwt(token, {
    ...sessionFallbacks(),
    retailerId: fallbackRoomId || getCurrentVoiceRetailerRoomId()
  });

  const payload = {
    room: retailerId || (fallbackRoomId ? String(fallbackRoomId) : '')
  };
  const tabSessionId = getOrCreateVoiceTabSessionId();
  if (tabSessionId) {
    payload.tab_session_id = tabSessionId;
  }
  if (retailerUserId) {
    payload.retailer_user_id = retailerUserId;
  }

  return payload;
}

/**
 * Shared fields for POST /voice/calls, /answer and /hangup.
 *
 * @returns {Promise<{ tab_session_id: string, retailer_user_id?: string }>}
 */
export async function buildVoiceCallControlBody() {
  const token = await getMercatelySessionJwt();
  const { retailerUserId } = getVoiceIdentityFromJwt(token, sessionFallbacks());
  const body = { tab_session_id: getOrCreateVoiceTabSessionId() };
  if (retailerUserId) {
    body.retailer_user_id = retailerUserId;
  }
  return body;
}
